import { BellLight, BitLight, CloseMiniLine, UserLight } from '@repo/go-icons';
import { Button } from '@repo/go-ui';
import { clsx } from 'clsx';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { GLOBAL_DEPTH_UNIT } from 'src/api/consts';
import { useService } from 'src/packages/di';

import type { TNotification } from '../../AnomalyNotificationsService';

import styles from './AlertNotification.module.scss';

type AlertNotificationRiskLevel = TNotification['riskLevel'];

type AlertNotificationProps = {
  className?: string;
  notification: TNotification;
  onClose?: () => void;
};

const RISK_CONFIG: Record<AlertNotificationRiskLevel, { className?: string; labelKey: string | null }> = {
  NO_RISK: {
    labelKey: null,
  },
  LOW_RISK: {
    className: styles.riskLow,
    labelKey: 'anomalyNotifications:risk.low',
  },
  MED_RISK: {
    className: styles.riskMedium,
    labelKey: 'anomalyNotifications:risk.medium',
  },
  HIGH_RISK: {
    className: styles.riskHigh,
    labelKey: 'anomalyNotifications:risk.high',
  },
};

function formatDepth(value: number) {
  const formattedValue = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
    useGrouping: false,
  }).format(value);
  return `${formattedValue} ${GLOBAL_DEPTH_UNIT}`;
}

function formatOperationPath(notification: TNotification) {
  return [notification.sectionTypeName, notification.runTypeName, notification.operationType]
    .filter((value): value is string => value !== null)
    .join(' / ');
}

export const AlertNotification = observer(function AlertNotification({
  className,
  notification,
  onClose,
}: AlertNotificationProps) {
  const { t } = useTranslation();
  const alertsPersonalPreferenceService = useService('alertsPersonalPreference');
  const showRecommendations =
    notification.alertType === 'ALERT_RULE'
      ? alertsPersonalPreferenceService.preferences.alertTypes.alertRules.showRecommendations
      : alertsPersonalPreferenceService.preferences.alertTypes.manual.showRecommendations;
  const SourceIcon = notification.alertType === 'MANUAL' ? UserLight : BellLight;
  const riskConfig = RISK_CONFIG[notification.riskLevel];
  const notiSize = notification.notificationSize;
  const operationPath = formatOperationPath(notification);

  return (
    <article className={clsx(styles.notification, riskConfig.className, className)}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.leftBlock}>
            <SourceIcon className={styles.icon} />
            <span className={styles.wellName}>{notification.wellName}</span>
          </div>
          {notiSize === 'SMALL' && (
            <div className={styles.leftBlock}>
              <BitLight className={styles.icon} />
              <span>{formatDepth(notification.bitDepth)}</span>
            </div>
          )}
        </div>
        <div className={styles.headerRight}>
          {riskConfig.labelKey && <span className={styles.riskLabel}>{t(riskConfig.labelKey)}</span>}

          {onClose && (
            <Button
              className={styles.closeButton}
              icon={<CloseMiniLine className={styles.closeIcon} />}
              type="button"
              variant="flat"
              onClick={onClose}
            />
          )}
        </div>
      </header>
      <div className={styles.content}>
        {notiSize === 'LARGE' && (
          <div className={styles.additionalInfo}>
            <div className={styles.depthsBlock}>
              <div className={styles.depthItem}>
                <span className={styles.depthLabel}>{t('anomalyNotifications:depth.hole')}</span>
                <span className={styles.depthValue}>{formatDepth(notification.holeDepth)}</span>
              </div>
              <div className={styles.depthItem}>
                <span className={styles.depthLabel}>{t('anomalyNotifications:depth.tool')}</span>
                <span className={styles.depthValue}>{formatDepth(notification.bitDepth)}</span>
              </div>
            </div>
            {operationPath && <div className={styles.operationPath}>{operationPath}</div>}
          </div>
        )}
        <p className={styles.message}>{notification.notification}</p>

        {showRecommendations && notification.recommendation && (
          <div className={styles.recommendation}>{notification.recommendation}</div>
        )}
      </div>
    </article>
  );
});
